import React, {useEffect, useRef, useState} from "react";
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Spinner,
    Text,
    useColorModeValue,
    useToast
} from "@chakra-ui/react";
import ColumnsTableManageRoles from "./components/ColumnsTableManageRoles";
import {columnsDataManageRole} from "./variables/columnsDataManageRole";
import {FaPlus} from "react-icons/fa";
import {MdAdminPanelSettings, MdChevronLeft, MdChevronRight, MdFilterList, MdRefresh, MdSearch} from "react-icons/md";
import Card from "components/card/Card";
import axios from "axios";
import Select from "react-select";
import makeAnimated from "react-select/animated";

const animatedComponents = makeAnimated();

export default function Settings({onDataUpdate}) {
    const [tableData, setTableData] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({name: ""});
    const [inputValues, setInputValues] = useState({name: ""});
    const [user, setUser] = useState([]);
    const [role, setRole] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newData, setNewData] = useState({user: "", role: []});
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const debounceRef = useRef(null);
    const [isSearching, setIsSearching] = useState(false);

    const inputBgColor = useColorModeValue("white", "gray.700");
    const inputBorderColor = useColorModeValue("gray.200", "gray.600");
    const headingColor = useColorModeValue("navy.700", "white");
    const mutedTextColor = useColorModeValue("secondaryGray.600", "secondaryGray.500");
    const filterBgColor = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const iconBgColor = useColorModeValue("brand.100", "whiteAlpha.100");
    const modalBg = useColorModeValue("white", "navy.800");
    const selectBg = useColorModeValue("white", "navy.700");
    const selectText = useColorModeValue("gray.700", "white");
    const selectBorder = useColorModeValue("gray.200", "gray.600");
    const selectHover = useColorModeValue("gray.100", "whiteAlpha.100");
    const selectPlaceholder = useColorModeValue("gray.400", "gray.400");

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: "40px",
            backgroundColor: selectBg,
            borderColor: state.isFocused ? "#3182ce" : selectBorder,
            boxShadow: state.isFocused ? "0 0 0 1px #3182ce" : "none",
            ":hover": {borderColor: state.isFocused ? "#3182ce" : selectBorder}
        }),
        menu: (provided) => ({...provided, backgroundColor: selectBg, zIndex: 10}),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? "#3182ce" : state.isFocused ? selectHover : selectBg,
            color: state.isSelected ? "white" : selectText,
            ":active": {backgroundColor: "#3182ce"}
        }),
        singleValue: (provided) => ({...provided, color: selectText}),
        input: (provided) => ({...provided, color: selectText}),
        placeholder: (provided) => ({...provided, color: selectPlaceholder}),
        multiValue: (provided) => ({...provided, backgroundColor: filterBgColor}),
        multiValueLabel: (provided) => ({...provided, color: selectText}),
        multiValueRemove: (provided) => ({...provided, color: selectText, ":hover": {backgroundColor: "red.400", color: "white"}})
    };

    useEffect(() => {
        const token = document.cookie.split("; ").find(row => row.startsWith("token=")).split("=")[1];
        setIsLoading(true);
        setLoadError("");

        fetch(process.env.REACT_APP_API_URL + `/v1/users?page=${pageIndex + 1}&size=${pageSize}&name=${filters.name}`, {
            headers: {Authorization: `Bearer ${token}`},
        })
            .then((response) => {
                if (!response.ok) throw new Error("Failed to fetch users data");
                return response.json();
            })
            .then((data) => {
                if (!data.success) throw new Error(data.message || "Failed to fetch users data");
                const users = data.data.content.map((userData) => ({
                    ...userData,
                    userName: userData.name,
                    userEmail: userData.email,
                    roleName: userData.userRoles.map((userRole) => userRole.role.roleName).join(" | "),
                }));
                setTableData(users);
                setTotalPages(data.data.totalPages);
            })
            .catch((error) => setLoadError(error.message || "Unable to load user role data."))
            .finally(() => setIsLoading(false));
    }, [pageIndex, pageSize, filters]);

    useEffect(() => {
        const token = document.cookie.split("; ").find(row => row.startsWith("token=")).split("=")[1];
        fetch(process.env.REACT_APP_API_URL + "/v1/users?page=1&size=9999", {headers: {Authorization: `Bearer ${token}`}})
            .then(response => response.json())
            .then(data => setUser(data.data.content));
        fetch(process.env.REACT_APP_API_URL + "/v1/role?page=1&size=9999", {headers: {Authorization: `Bearer ${token}`}})
            .then(response => response.json())
            .then(data => setRole(data.data.content));
    }, []);

    const handleDataUpdate = () => {
        const token = document.cookie.split("; ").find(row => row.startsWith("token=")).split("=")[1];
        setIsLoading(true);
        setLoadError("");

        fetch(process.env.REACT_APP_API_URL + `/v1/users?page=${pageIndex + 1}&size=${pageSize}&name=${filters.name}`, {
            headers: {Authorization: `Bearer ${token}`},
        })
            .then((response) => {
                if (!response.ok) throw new Error("Failed to fetch users data");
                return response.json();
            })
            .then((data) => {
                if (!data.success) throw new Error(data.message || "Failed to fetch users data");
                const users = data.data.content.map((userData) => ({
                    ...userData,
                    userName: userData.name,
                    userEmail: userData.email,
                    roleName: userData.userRoles.map((userRole) => userRole.role.roleName).join(" | "),
                }));
                setTableData(users);
                setTotalPages(data.data.totalPages);
            })
            .catch((error) => setLoadError(error.message || "Unable to load user role data."))
            .finally(() => setIsLoading(false));
    };

    const handleFilterChange = (event) => {
        const {name, value} = event.target;
        setInputValues(prev => ({...prev, [name]: value}));
        setIsSearching(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setFilters(prev => ({...prev, [name]: value}));
            setPageIndex(0);
            setIsSearching(false);
        }, 1000);
    };

    const resetFilters = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setInputValues({name: ""});
        setFilters({name: ""});
        setPageIndex(0);
        setIsSearching(false);
    };

    const handleUserChange = async (selectedOption) => {
        try {
            const response = await axios.get(process.env.REACT_APP_API_URL + `/v1/user-roles?userId=${selectedOption.value}`);
            if (response.status === 200) {
                const roles = response.data.data.map(item => item.role.id);
                setNewData({...newData, user: selectedOption ? selectedOption.value : "", role: roles});
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        console.log("Selected Role:", selectedOption);
    };

    const handleRoleChange = (selectedOptions) => {
        setNewData({...newData, role: selectedOptions ? selectedOptions.map(option => option.value) : []});
    };

    const toast = useToast();
    const handleAddData = async () => {
        const token = document.cookie.split("; ").find(row => row.startsWith("token=")).split("=")[1];
        try {
            const transformedData = newData.role.map(roleId => ({roleId: roleId, userId: newData.user}));
            const oldData = await axios.get(process.env.REACT_APP_API_URL + "/v1/user-roles?userId=" + newData.user);
            const existingRoleIds = oldData.data.data.map(item => item.role.id);
            const rolesToDelete = existingRoleIds.filter(id => !newData.role.includes(id));
            for (const roleId of rolesToDelete) {
                await axios.delete(process.env.REACT_APP_API_URL + "/v1/user-role", {
                    headers: {Authorization: `Bearer ${token}`},
                    data: {userId: newData.user, roleId: roleId}
                });
            }
            const response = await axios.post(process.env.REACT_APP_API_URL + "/v1/user-roles", transformedData, {
                headers: {Authorization: `Bearer ${token}`}
            });
            if (response.status === 200) {
                toast({title: "User Role created.", description: "You data successfully saved. Please refresh page.", status: "success", duration: 5000, isClosable: true});
                setNewData({user: "", role: []});
                setIsAddModalOpen(false);
                handleDataUpdate();
            }
        } catch (error) {
            toast({title: "Error", description: "There was an issue creating your account.", status: "error", duration: 5000, isClosable: true});
        }
    };

    const hasActiveFilters = Object.values(inputValues).some(Boolean);

    return (
        <Box pt={{base: "130px", md: "80px", xl: "80px"}}>
            <Card mb="24px" p={{base: "20px", md: "24px"}}>
                <Flex direction={{base: "column", md: "row"}} justify="space-between" align={{base: "flex-start", md: "center"}} gap={5}>
                    <Flex align="center" gap={4}>
                        <Flex w="48px" h="48px" borderRadius="16px" bg={iconBgColor} align="center" justify="center">
                            <Icon as={MdAdminPanelSettings} color="brand.500" boxSize={6}/>
                        </Flex>
                        <Box>
                            <Heading as="h1" size="lg" color={headingColor}>User Role Management</Heading>
                            <Text color={mutedTextColor} mt={1} fontSize="sm">Atur role untuk setiap user dalam satu workspace.</Text>
                        </Box>
                    </Flex>
                    <Flex direction={{base: "column", sm: "row"}} w={{base: "full", md: "auto"}} gap={3}>
                        <Button variant="light" size="sm" leftIcon={<Icon as={MdRefresh}/>} onClick={handleDataUpdate} isLoading={isLoading}>Refresh data</Button>
                        <Button colorScheme="brand" size="sm" leftIcon={<FaPlus/>} onClick={() => setIsAddModalOpen(true)}>Tambah Data</Button>
                    </Flex>
                </Flex>
            </Card>

            <Card mb="24px" p={{base: "20px", md: "24px"}}>
                <Flex justify="space-between" align={{base: "flex-start", sm: "center"}} direction={{base: "column", sm: "row"}} gap={3} mb={5}>
                    <Flex align="center" gap={2}>
                        <Icon as={MdFilterList} color="brand.500" boxSize={5}/>
                        <Box>
                            <Heading as="h2" size="sm" color={headingColor}>Filter user role</Heading>
                            <Text color={mutedTextColor} fontSize="sm">Cari user berdasarkan nama.</Text>
                        </Box>
                    </Flex>
                    <Button variant="ghost" size="sm" onClick={resetFilters} isDisabled={!hasActiveFilters}>Reset filter</Button>
                </Flex>
                <SimpleGrid columns={{base: 1, md: 2}} spacing={4} bg={filterBgColor} p={{base: 3, md: 4}} borderRadius="16px">
                    <FormControl maxW={{md: "420px"}}>
                        <FormLabel htmlFor="user-role-name-filter" fontSize="sm" color={headingColor}>Nama user</FormLabel>
                        <InputGroup>
                            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.500"/></InputLeftElement>
                            <Input id="user-role-name-filter" placeholder="Cari nama user" name="name" value={inputValues.name} onChange={handleFilterChange} bg={inputBgColor} borderColor={inputBorderColor}/>
                            {isSearching && <InputRightElement><Spinner size="sm" color="brand.500"/></InputRightElement>}
                        </InputGroup>
                    </FormControl>
                </SimpleGrid>
            </Card>

            {loadError ? (
                <Card p={{base: "20px", md: "24px"}}>
                    <Alert status="error" borderRadius="12px" alignItems="flex-start">
                        <AlertIcon mt={1}/>
                        <Box><Text fontWeight="700">Data user role tidak dapat dimuat</Text><Text fontSize="sm" mt={1}>{loadError}</Text><Button mt={4} size="sm" colorScheme="red" leftIcon={<Icon as={MdRefresh}/>} onClick={handleDataUpdate}>Coba lagi</Button></Box>
                    </Alert>
                </Card>
            ) : (
                <>
                    <ColumnsTableManageRoles columnsData={columnsDataManageRole} tableData={tableData} onDataUpdate={handleDataUpdate} isLoading={isLoading}/>
                    <Card p={{base: "16px", md: "20px"}}>
                        <Flex direction={{base: "column", sm: "row"}} justify="space-between" align="center" gap={4}>
                            <Button variant="light" size="sm" leftIcon={<Icon as={MdChevronLeft}/>} onClick={() => setPageIndex(prev => Math.max(prev - 1, 0))} isDisabled={pageIndex === 0 || isLoading}>Sebelumnya</Button>
                            <Text color={mutedTextColor} fontSize="sm" textAlign="center">Halaman <Text as="span" color={headingColor} fontWeight="700">{pageIndex + 1}</Text> dari <Text as="span" color={headingColor} fontWeight="700">{totalPages}</Text></Text>
                            <Button variant="light" size="sm" rightIcon={<Icon as={MdChevronRight}/>} onClick={() => setPageIndex(prev => Math.min(prev + 1, totalPages - 1))} isDisabled={pageIndex + 1 === totalPages || isLoading}>Berikutnya</Button>
                        </Flex>
                    </Card>
                </>
            )}

            <Modal isOpen={isAddModalOpen} onClose={() => { setIsAddModalOpen(false); setNewData({user: "", role: []}); }} isCentered scrollBehavior="inside">
                <ModalOverlay/>
                <ModalContent bg={modalBg} borderRadius="20px" mx={4}>
                    <ModalHeader borderBottomWidth="1px" borderColor={inputBorderColor}>Add Data User Role</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody py={6}>
                        <FormControl mb={5}><FormLabel htmlFor="user-role-user-select">User</FormLabel><Select inputId="user-role-user-select" name="user" value={user.map((userData) => ({value: userData.id, label: userData.email})).find(option => option.value === newData.user)} onChange={handleUserChange} options={user.map((userData) => ({value: userData.id, label: userData.email}))} styles={selectStyles} components={animatedComponents}/></FormControl>
                        <FormControl><FormLabel htmlFor="user-role-role-select">Role</FormLabel><Select inputId="user-role-role-select" name="role" isMulti value={role.filter(roleData => Array.isArray(newData.role) && newData.role.includes(roleData.id)).map(roleData => ({value: roleData.id, label: roleData.roleName}))} onChange={handleRoleChange} options={role.map((roleData) => ({value: roleData.id, label: roleData.roleName}))} styles={selectStyles} components={animatedComponents} isSearchable closeMenuOnSelect={false}/></FormControl>
                    </ModalBody>
                    <ModalFooter borderTopWidth="1px" borderColor={inputBorderColor} flexDirection={{base: "column-reverse", sm: "row"}} gap={3}>
                        <Button variant="ghost" onClick={() => { setIsAddModalOpen(false); setNewData({user: "", role: []}); }}>Cancel</Button>
                        <Button colorScheme="blue" onClick={() => { setIsAddModalOpen(false); let Swal = require("sweetalert2"); Swal.fire({title: "Are you sure?", text: "Please confirm to save this data", icon: "warning", showCancelButton: true, confirmButtonColor: "#3085d6", cancelButtonColor: "#d33", confirmButtonText: "Yes, save it!"}).then((result) => { if (result.isConfirmed) { handleAddData(); } else { setIsAddModalOpen(true); } }); }} isDisabled={!newData.role || !newData.user}>Add Data</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
